import React, { PureComponent } from 'react';
import {
  View,
  ActivityIndicator,
  StyleSheet,
  InteractionManager,
} from 'react-native';
import { connect } from 'react-redux';
import { SignupScreen } from 'fitbeat.gym-core';
import { UserServiceClient } from 'fitbeat.api-clients';
import { NavigationEventSubscription } from 'react-navigation';
import { Logger } from '../../../utils/Logger';
import ConnectedAppsManagerSingleton, { IAppForOnboarding } from '../../../ConnectedApps';
import { getNextStep, navigateToNextStep } from '../../actions';
import { INavigateToNextStep } from '../../types';
import Theme from '../../../theme';
import { IAppStore } from '../../../HomeWorkouts';
import { UserDataConsumer } from '../../../utils';

const GARMIN_CONNECT_ROUTE = 'GarminConnectOnboardingFlow';
const APPLE_HEALTH_ROUTE = 'AppleHealthOnboardingFlow';
const HEALTH_CONNECT_ROUTE = 'HealthConnectOnboardingFlow';

interface IProps {
  navigation: any;
  user: { userProfileService: UserServiceClient };
  navigateToNextStep: (navigateToNextStep: any) => void;
  getNextStep(navigateToNextStep: INavigateToNextStep): void;
}

interface IState {
  screenHasBeenSeen: boolean;
}

class ConnectedAppEntryPoint extends PureComponent<IProps, IState> {
  private hasBeenSeenSubscription?: NavigationEventSubscription;

  public constructor(props: IProps) {
    super(props);
    this.state = {
      screenHasBeenSeen: false,
    };
  }

  public componentDidMount() {
    this.hasBeenSeenSubscription = this.props.navigation.addListener(
      'didFocus',
      async () => {
        if (this.state.screenHasBeenSeen) {
          this.props.navigation.goBack();
          return;
        }
        try {
          await this.decideAndNavigate();
        } catch (error) {
          Logger.leaveBreadcrumb('[ConnectedAppEntryPointScreen] Error in decideAndNavigate');
          Logger.notify(error);
          this.skipConnectedAppOnboarding();
        }
      },
    );
  }

  public componentWillUnmount() {
    if (this.hasBeenSeenSubscription) {
      this.hasBeenSeenSubscription.remove();
    }
  }

  public render() {
    return (
      <View style={styles.container}>
        <ActivityIndicator size='small' color={Theme.PRIMARY} />
      </View>
    );
  }

  private skipConnectedAppOnboarding = () => {
    navigateToNextOnboardingStep(
      { ...this.props, appForOnboarding: undefined },
      this.props.getNextStep,
      this.props.navigateToNextStep,
    );
  };

  private decideAndNavigate = async () => {
    const { navigation } = this.props;
    try {
      const appInfo: IAppForOnboarding | undefined = await ConnectedAppsManagerSingleton.getAppForOnboarding();
      let nextRouteName: string | undefined;
      Logger.leaveBreadcrumb(`[ConnectedAppEntryPointScreen] app for onboarding: ${JSON.stringify(appInfo)}`);

      if (appInfo) {
        switch (appInfo.app) {
          case 'Garmin Connect':
            nextRouteName = GARMIN_CONNECT_ROUTE;
            break;
          case 'Apple Health':
            nextRouteName = APPLE_HEALTH_ROUTE;
            break;
          case 'Health Connect':
            nextRouteName = HEALTH_CONNECT_ROUTE;
            break;
          default:
            Logger.notify(new Error(`App found but not handled: ${appInfo.app}`));
        }
      }

      if (nextRouteName) {
        await InteractionManager.runAfterInteractions(() => {
          navigation.replace(nextRouteName, { appForOnboarding: appInfo });
        });
      } else {
        Logger.notify(new Error('No app for onboarding.'));
        this.setState({ screenHasBeenSeen: true }, async () => {
          await InteractionManager.runAfterInteractions(() => {
            this.skipConnectedAppOnboarding();
          });
        });
      }
    } catch (error) {
      Logger.notify(error);
      this.setState({ screenHasBeenSeen: true }, async () => {
        await InteractionManager.runAfterInteractions(() => {
          this.skipConnectedAppOnboarding();
        });
      });
    }
  };
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Theme.WHITE,
  },
});

export const navigateToNextOnboardingStep = (
  props: IConnectedAppOnboardingProps,
  getNextStepInOnboarding: (navigateToNextStep: any) => void,
  navigateToNextStepFromComponent: (navigation: any) => void,
) => {
  const { user, navigation } = props;

  getNextStepInOnboarding({
    currentScreen: SignupScreen.ConnectedAppEntryPoint,
    userProfile: {},
    userServiceClient: user.userProfileService,
  });
  navigateToNextStepFromComponent(navigation);
};

export interface IConnectedAppOnboardingProps {
  navigation: any;
  user: { userProfileService: UserServiceClient };
  appForOnboarding: IAppForOnboarding | undefined;
  navigateToNextStep: (navigateToNextStep: any) => void;
  getNextStep(navigateToNextStep: INavigateToNextStep): void;
}

const mapStateToProps = (state: IAppStore) => ({
  enabledFeatures: state.auth.signup.enabledFeatures,
});

const mapDispatchToProps = {
  getNextStep,
  navigateToNextStep,
};

export const ConnectedAppEntryPointScreen = UserDataConsumer(
  connect(mapStateToProps, mapDispatchToProps)(ConnectedAppEntryPoint),
);

export default ConnectedAppEntryPointScreen;
